import { useState, useMemo } from 'react';
import { Plus, Send, XCircle, Trash2 } from 'lucide-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import {
  useCampaigns,
  useSendCampaign,
  useCancelCampaign,
  useDeleteCampaign,
  useCampaignStatus,
  type Campaign,
} from '@/hooks/customer-engagement/use-campaigns';
import { formatDateTime } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-error';
import { usePermissions } from '@/hooks/use-permissions';
import { CampaignCreateDialog } from './campaign-create-dialog';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export function CampaignListSection() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [toSend, setToSend] = useState<Campaign | null>(null);
  const [toCancel, setToCancel] = useState<Campaign | null>(null);

  const [toDelete, setToDelete] = useState<Campaign | null>(null);

  const { data, isLoading, isError, error } = useCampaigns();
  const send = useSendCampaign();
  const cancel = useCancelCampaign();
  const del = useDeleteCampaign();
  const permissions = usePermissions();
  const canCreate = permissions.has('campaigns.create');
  const canSend = permissions.has('campaigns.send');
  const canCancel = permissions.has('campaigns.cancel');
  const canDelete = permissions.has('campaigns.delete');
  const campaigns = data?.data ?? [];
  const total = campaigns.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => campaigns.slice((page - 1) * perPage, page * perPage), [campaigns, page, perPage]);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const columns: ColumnDef<Campaign>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium text-foreground">{row.original.name}</div>
          {row.original.scheduledAt && (
            <div className="text-[11px] text-muted-foreground">
              Scheduled: {formatDateTime(row.original.scheduledAt)}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'channel',
      header: 'Channel',
      cell: ({ row }) => (
        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
          {row.original.channel}
        </span>
      ),
    },
    {
      accessorKey: 'audienceType',
      header: 'Audience',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {row.original.audienceType === 'all'
            ? 'All opted-in'
            : row.original.audienceType === 'segment'
              ? 'Segment'
              : 'Manual list'}
        </span>
      ),
    },
    {
      id: 'progress',
      header: 'Progress',
      cell: ({ row }) => <ProgressCell campaign={row.original} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.original.updatedAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          {canSend && (row.original.status === 'draft' || row.original.status === 'failed') && (
            <button
              type="button"
              onClick={() => setToSend(row.original)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
            >
              <Send className="h-3.5 w-3.5" /> Send Now
            </button>
          )}
          {canCancel &&
            (row.original.status === 'scheduled' ||
              row.original.status === 'in_progress' ||
              row.original.status === 'paused') && (
              <button
                type="button"
                onClick={() => setToCancel(row.original)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
              >
                <XCircle className="h-3.5 w-3.5" /> Cancel
              </button>
            )}
          {/* Delete only for non-active campaigns; queued/sending must be cancelled first (backend enforces this too). */}
          {canDelete && !['scheduled', 'in_progress', 'sending', 'paused'].includes(row.original.status) && (
            <button
              type="button"
              onClick={() => setToDelete(row.original)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
              title="Delete campaign"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pageRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Campaigns</h2>
          <p className="mt-1 text-sm text-gray-600">
            Email and SMS campaigns. Manual list uploads (CSV / Excel) auto-enroll unknown phones; existing opt-outs are
            respected. Scheduled SMS campaigns are paced across the start → finish window.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        )}
      </div>

      {isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load: {apiErrorMessage(error)}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        !isError && (
          <>
            <DataTable table={table} columns={columns} />
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )
      )}

      <CampaignCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmDialog
        open={!!toSend}
        title="Send campaign now"
        message={toSend ? `Send "${toSend.name}" immediately to all opted-in recipients? This cannot be undone.` : ''}
        confirmLabel={send.isPending ? 'Sending…' : 'Send Now'}
        variant="warning"
        loading={send.isPending}
        onConfirm={async () => {
          if (!toSend) return;
          try {
            const res = await send.mutateAsync(toSend.id);
            if (toSend.channel === 'sms') {
              // SMS delivers through the paced worker — sending starts now and
              // progresses asynchronously; the Progress column tracks it.
              toast.success(`"${toSend.name}" is sending to ${res.data.total} recipient(s) — watch Progress.`);
            } else {
              toast.success(`Sent ${res.data.sent} of ${res.data.total} (${res.data.failed} failed)`);
            }
            setToSend(null);
          } catch (e) {
            toast.error(apiErrorMessage(e, 'Failed to send'));
          }
        }}
        onCancel={() => setToSend(null)}
      />

      <ConfirmDialog
        open={!!toCancel}
        title="Cancel campaign"
        message={toCancel ? `Cancel "${toCancel.name}"? Already-sent messages can't be recalled.` : ''}
        confirmLabel={cancel.isPending ? 'Cancelling…' : 'Cancel campaign'}
        variant="danger"
        loading={cancel.isPending}
        onConfirm={async () => {
          if (!toCancel) return;
          try {
            await cancel.mutateAsync(toCancel.id);
            toast.success('Campaign cancelled');
            setToCancel(null);
          } catch (e) {
            toast.error(apiErrorMessage(e, 'Failed to cancel'));
          }
        }}
        onCancel={() => setToCancel(null)}
      />

      <ConfirmDialog
        open={!!toDelete}
        title="Delete campaign"
        message={toDelete ? `Permanently delete "${toDelete.name}" and its recipient list? This can't be undone.` : ''}
        confirmLabel={del.isPending ? 'Deleting…' : 'Delete'}
        variant="danger"
        loading={del.isPending}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await del.mutateAsync(toDelete.id);
            toast.success(`Deleted "${toDelete.name}".`);
            setToDelete(null);
          } catch (e) {
            toast.error(apiErrorMessage(e, 'Failed to delete'));
          }
        }}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}

function ProgressCell({ campaign }: { campaign: Campaign }) {
  const live = campaign.status === 'in_progress' || campaign.status === 'scheduled' || campaign.status === 'paused';
  const status = useCampaignStatus(live ? campaign.id : undefined, { refetchMs: live ? 3_000 : undefined });
  const s = status.data?.data;

  const target = s?.targetCount ?? campaign.targetCount;
  const sent = s?.sentCount ?? campaign.sentCount;
  const failed = s?.failedCount ?? campaign.failedCount;
  const skipped = s?.skippedCount ?? campaign.skippedCount;
  const processed = sent + failed + skipped;
  const pct = target > 0 ? Math.round((processed / target) * 100) : 0;

  if (target === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const eta = s?.estimatedCompletionAt ? formatDateTime(s.estimatedCompletionAt) : null;

  return (
    <div className="min-w-[160px]">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-2 rounded-full ${campaign.status === 'failed' ? 'bg-red-500' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {processed.toLocaleString()} / {target.toLocaleString()} ({pct}%)
        </span>
        <span>
          {sent.toLocaleString()} sent
          {failed > 0 ? ` · ${failed.toLocaleString()} failed` : ''}
          {skipped > 0 ? ` · ${skipped.toLocaleString()} skipped` : ''}
        </span>
      </div>
      {eta && campaign.status === 'in_progress' && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">ETA: {eta}</div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Campaign['status'] }) {
  const tone =
    status === 'sent'
      ? 'bg-green-100 text-green-800'
      : status === 'in_progress' || status === 'sending'
        ? 'bg-blue-100 text-blue-800'
        : status === 'scheduled'
          ? 'bg-purple-100 text-purple-800'
          : status === 'failed'
            ? 'bg-red-100 text-red-800'
            : status === 'paused'
              ? 'bg-orange-100 text-orange-800'
              : status === 'cancelled'
                ? 'bg-gray-300 text-gray-800'
                : 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{status}</span>;
}
